'use client';

import { TwoColumnLayout } from '@components/layout/Layouts/TwoColumnLayout';
import { CompanyJobsSidebar } from '@components/features/company-jobs-sidebar/CompanyJobsSidebar';
import { CandidateJobsSidebar } from '@components/features/candidate-jobs-sidebar';
import { SidebarNavigationProvider } from '@components/contexts/SidebarNavigationContext';
import { FilterDrawerProvider } from '@components/contexts/FilterDrawerContext';
import { MissionControlBestieChatProvider } from '@components/contexts/MissionControlBestieChatContext';
import { CompanyApplicantProvider } from '@components/contexts/CompanyApplicantContext';
import StoriesPageCards from '@components/templates/stories/StoriesPageCards';
import { useMissionControlContext } from '@hooks/features/useMissionControlContext';

interface MissionControlStoriesProps {
  locale: Locale;
  isAuthenticated?: boolean;
  initialData?: ApiSchema<'ResumeStoriesResult'>;
  ps?: string;
}

const storiesContent = (initialData: ApiSchema<'ResumeStoriesResult'> | undefined, locale: Locale, ps?: string) => (
  <section className="bg-surface-100 md:py-10">
    <StoriesPageCards locale={locale} initialData={initialData} ps={ps} />
  </section>
);

/**
 * @description Mission Control layout with Stories as the main content panel.
 * Respects the user's current context (candidate / company) for the sidebar,
 * but always renders StoriesPageCards in the main content area.
 */
export const MissionControlStories = (props: MissionControlStoriesProps) => {
  const {
    locale, isAuthenticated = false, initialData, ps,
  } = props;

  const { currentContext, switchContext } = useMissionControlContext();

  if (currentContext === 'company') {
    return (
      <CompanyApplicantProvider>
        <TwoColumnLayout
          leftSidebar={(
            <CompanyJobsSidebar
              locale={locale}
              isAuthenticated={isAuthenticated}
              onSwitchContext={switchContext}
            />
          )}
          mainContent={storiesContent(initialData, locale, ps)}
          rightSidebar={undefined}
        />
      </CompanyApplicantProvider>
    );
  }

  // Candidate context (default)
  return (
    <SidebarNavigationProvider initialView="new-search" locale={locale}>
      <FilterDrawerProvider>
        <MissionControlBestieChatProvider>
          <TwoColumnLayout
            leftSidebar={(
              <CandidateJobsSidebar
                locale={locale}
                isAuthenticated={isAuthenticated}
                onSwitchContext={switchContext}
              />
            )}
            mainContent={storiesContent(initialData, locale, ps)}
            rightSidebar={undefined}
          />
        </MissionControlBestieChatProvider>
      </FilterDrawerProvider>
    </SidebarNavigationProvider>
  );
};
